import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { setApiLocaleContext } from "../apiLocaleContext";
import { resolveCapabilities } from "../capabilityPolicies";
import { getLocaleMeta, normalizeLocale, resolveProductFromPathname } from "../routeLocale";
import { traceLocaleProvider } from "../routeTelemetry";

const ProductContext = createContext(null);

export function ProductContextProvider({ children, session = null, entitlements = null }) {
  const location = useLocation();

  const value = useMemo(() => {
    const resolved = resolveProductFromPathname(location.pathname);
    const locale = normalizeLocale(resolved.locale);
    const localeMeta = getLocaleMeta(locale);
    const capabilities = resolveCapabilities({
      locale,
      product: resolved.product,
      entitlements,
      session,
    });

    traceLocaleProvider({
      locale,
      direction: localeMeta.direction,
      product: resolved.product,
      pathname: location.pathname,
    });

    return {
      ...resolved,
      locale,
      localeMeta,
      capabilities,
      session,
      entitlements,
    };
  }, [location.pathname, session, entitlements]);

  useEffect(() => {
    setApiLocaleContext({ locale: value.locale, product: value.product });
  }, [value.locale, value.product]);

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function useProductContext() {
  const ctx = useContext(ProductContext);
  if (!ctx) {
    const fallbackPath =
      typeof window !== "undefined" ? window.location.pathname : "/";
    const resolved = resolveProductFromPathname(fallbackPath);
    const locale = normalizeLocale(resolved.locale);
    return {
      ...resolved,
      locale,
      localeMeta: getLocaleMeta(locale),
      capabilities: resolveCapabilities({
        locale,
        product: resolved.product,
      }),
      session: null,
      entitlements: null,
    };
  }
  return ctx;
}

export default ProductContext;
